import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import DataApi from '../../api/DataApi';
import AppToast from '../AppToast';
import './css/PermissionList.css';
import jwt_decode from 'jwt-decode';
import axios from 'axios';
import * as constants from '../../constants/CONSTANT';
import RecordViewLayoutTableSection from './RecordViewLayoutTableSection';
import FieldPermissionsModal from './FieldPermissionsModal';
import AssignPicklistValuesModal from './AssignPicklistValuesModal';
import { clearPermissionCache } from '../../utils/permissionCache';
import { filterOutSetupStandardObjects } from '../../utils/entitlements';

function parseRoleObjectConfig(raw) {
  if (raw == null) return {};
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) || {};
    } catch {
      return {};
    }
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) return { ...raw };
  return {};
}

const PermissionList = () => {
  const { id: roleId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [permissions, setPermissions] = useState([]);
  const [role, setRole] = useState(null);
  const [selectedTabNames, setSelectedTabNames] = useState([]);
  const [allowedStandardTabs, setAllowedStandardTabs] = useState([]);
  const [allowedCustomTabs, setAllowedCustomTabs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [userRole, setUserRole] = useState('');
  const [fieldPermissionsByObject, setFieldPermissionsByObject] = useState({});
  const [fieldModalPermission, setFieldModalPermission] = useState(null);
  const [picklistModalPermission, setPicklistModalPermission] = useState(null);
  const permissionApi = new DataApi('permissions');
  const permissionBulkApi = new DataApi('permissions/bulk');
  const roleApi = new DataApi('roles');

  useEffect(() => {
    if (roleId) {
      fetchRole();
      fetchPermissions();
    }
  }, [roleId]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwt_decode(token);
        console.log('Decoded token:', decoded);
        setUserRole(decoded?.role_name || '');
      } catch (err) {
        console.error('Error decoding token:', err);
      }
    }
  }, []);

  // Check for success message in URL parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const success = searchParams.get('success');
    const message = searchParams.get('message');

    if (success === 'true' && message) {
      setToastMessage(decodeURIComponent(message));
      setToastVariant('success');
      setShowToast(true);

      // Clear the URL parameters
      navigate(location.pathname, { replace: true });
    }
  }, [location.search, navigate, location.pathname]);

  const fetchRole = async () => {
    try {
      const response = await roleApi.fetchById(roleId);
      console.log('Role response:', response);
      if (response.data.success) {
        setRole(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching role:', err);
      setError('Failed to fetch role information');
    }
  };

  const fetchPermissions = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('Fetching permissions for roleId:', roleId);
      const response = await permissionApi.get(`/role/${roleId}`);
      console.log('Permissions response:', response);
      if (response.data.success) {
        setPermissions(filterOutSetupStandardObjects(response.data.data || []));
        setSelectedTabNames(Array.isArray(response.data.selectedTabNames) ? response.data.selectedTabNames : []);
        setAllowedStandardTabs(Array.isArray(response.data.allowedStandardTabs) ? response.data.allowedStandardTabs : []);
        setAllowedCustomTabs(Array.isArray(response.data.allowedCustomTabs) ? response.data.allowedCustomTabs : []);
        try {
          const token = localStorage.getItem('token');
          const fpRes = await axios.get(
            `${constants.API_BASE_URL}/api/permissions/role/${roleId}/field-permissions`,
            { headers: { Authorization: token } }
          );
          if (fpRes.data?.success) {
            setFieldPermissionsByObject(
              fpRes.data.data && typeof fpRes.data.data === 'object' ? fpRes.data.data : {}
            );
          }
        } catch (fpErr) {
          console.warn('Could not load field permissions:', fpErr);
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch permissions');
      console.error('Error fetching permissions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/setup/rolemanager');
  };

  const handleToastClose = () => {
    setShowToast(false);
  };

  // Selected tabs split by standard vs custom; resolve names to labels for display
  const matchTab = (name, tabList) =>
    tabList.find(
      (t) =>
        String(name).toLowerCase() === String(t.name || '').toLowerCase() ||
        String(name).toLowerCase() === String(t.label || '').toLowerCase()
    );
  const selectedStandardTabs = selectedTabNames
    .map((name) => matchTab(name, allowedStandardTabs))
    .filter(Boolean)
    .map((t) => ({ name: t.name, label: t.label || t.name }));
  const selectedCustomTabs = selectedTabNames
    .map((name) => matchTab(name, allowedCustomTabs))
    .filter(Boolean)
    .map((t) => ({ name: t.name, label: t.label || t.name }));

  const roleName = role?.fields?.find((f) => f.field_name === 'name')?.value;
  const isAdminEditingOwnRole =
    String(roleName || '').toUpperCase() === 'ADMIN' && String(userRole || '').toUpperCase() === 'ADMIN';
  /** FLS modal stays read-only for Admin-on-Admin; use bulk edit for object permissions. */
  const hideSelfAdminFieldModalEdits = isAdminEditingOwnRole;

  const handlePicklistConfigSave = async (objectName, nextConfig) => {
    const permission = permissions.find((p) => p.object_name === objectName);
    if (!permission?.id) {
      setToastMessage('Object permission not found; save picklists from bulk edit instead.');
      setToastVariant('danger');
      setShowToast(true);
      return;
    }
    const base = parseRoleObjectConfig(permission.config);
    const patch = nextConfig && typeof nextConfig === 'object' && !Array.isArray(nextConfig) ? nextConfig : {};
    const { picklists, ...rest } = patch;
    const merged = { ...base, ...rest };
    if (picklists !== undefined) {
      merged.picklists = picklists;
    }
    try {
      const res = await permissionBulkApi.upsert({
        permissions: [
          {
            id: permission.id,
            role_id: permission.role_id,
            object_name: permission.object_name,
            can_create: permission.can_create,
            can_read: permission.can_read,
            can_update: permission.can_update,
            can_delete: permission.can_delete,
            view_all: permission.view_all,
            modify_all: permission.modify_all,
            is_active: permission.is_active,
            config: merged,
          },
        ],
      });
      if (res.data?.success) {
        setPermissions((prev) =>
          prev.map((p) => (p.object_name === objectName ? { ...p, config: merged } : p))
        );
        setPicklistModalPermission((prev) =>
          prev && prev.object_name === objectName ? { ...prev, config: merged } : prev
        );
        clearPermissionCache();
        setToastMessage('Picklist assignment saved.');
        setToastVariant('success');
        setShowToast(true);
      } else {
        setToastMessage(res.data?.message || 'Failed to save picklist assignment');
        setToastVariant('danger');
        setShowToast(true);
      }
    } catch (e) {
      setToastMessage(e.response?.data?.message || e.message || 'Failed to save picklist assignment');
      setToastVariant('danger');
      setShowToast(true);
    }
  };

  const handleListFieldPermissionsSave = async (objectName, fieldMap) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(
        `${constants.API_BASE_URL}/api/permissions/role/${roleId}/field-permissions`,
        { fieldPermissionsByObject: { [objectName]: fieldMap } },
        { headers: { Authorization: token } }
      );
      if (res.data?.success) {
        const next = res.data.data && typeof res.data.data === 'object' ? res.data.data : {};
        setFieldPermissionsByObject(next);
        clearPermissionCache();
        setToastMessage('Field permissions saved.');
        setToastVariant('success');
        setShowToast(true);
      } else {
        setToastMessage(res.data?.message || 'Failed to save field permissions');
        setToastVariant('danger');
        setShowToast(true);
      }
    } catch (e) {
      setToastMessage(e.response?.data?.message || e.message || 'Failed to save field permissions');
      setToastVariant('danger');
      setShowToast(true);
    }
  };

  if (loading) {
    return (
      <div className="permission-list-page">
        <div className="loading">Loading permissions...</div>
      </div>
    );
  }

  return (
    <div className="permission-list-page">
      <div className="permission-list-header">
        <div className="header-left">
          <button className="btn-back" onClick={handleBack}>
            <i className="fas fa-arrow-left"></i>
            Back to Roles
          </button>
          <div className="header-info">
            <h1>View Permissions</h1>
            <p>Role: {role?.fields?.find(f => f.field_name === 'name')?.value || role?.name || 'Loading...'}</p>
          </div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {!loading && permissions.length === 0 && (
        <div className="no-permissions">
          <div className="permissions-tabs-section permissions-tabs-two-sections">
            <div className="tabs-subsection">
              <h4 className="tabs-subsection-title">Standard Tabs</h4>
              <div className="tabs-label-list">
                {selectedStandardTabs.length === 0 ? (
                  <span className="tabs-empty-hint">None selected</span>
                ) : (
                  selectedStandardTabs.map((t) => (
                    <span key={t.name} className="tab-label">
                      {t.label}
                    </span>
                  ))
                )}
              </div>
            </div>
            <div className="tabs-subsection">
              <h4 className="tabs-subsection-title">Custom Tabs</h4>
              <div className="tabs-label-list">
                {selectedCustomTabs.length === 0 ? (
                  <span className="tabs-empty-hint">None selected</span>
                ) : (
                  selectedCustomTabs.map((t) => (
                    <span key={t.name} className="tab-label">
                      {t.label}
                    </span>
                  ))
                )}
              </div>
            </div>
          </div>
          <p>No object permissions found for this role.</p>
          <button className="btn btn-primary" onClick={() => navigate(`/setup/permission/${roleId}/create`)}>
            Add First Permission
          </button>
          <button className="btn btn-secondary ms-2" onClick={() => navigate(`/setup/permission/${roleId}/bulk-edit`)}>
            Edit Permissions (incl. Tabs)
          </button>
        </div>
      )}

      {!loading && permissions.length > 0 && (
        <div className="permissions-content">
          <div className="permissions-tabs-actions">
            <button className="btn btn-success" onClick={() => navigate(`/setup/permission/${roleId}/bulk-edit`)}>
              Edit Permissions
            </button>
          </div>
          <div className="permissions-tabs-section permissions-tabs-two-sections">
            <div className="tabs-subsection">
              <h4 className="tabs-subsection-title">Standard Tabs</h4>
              <div className="tabs-label-list">
                {selectedStandardTabs.length === 0 ? (
                  <span className="tabs-empty-hint">None selected</span>
                ) : (
                  selectedStandardTabs.map((t) => (
                    <span key={t.name} className="tab-label">
                      {t.label}
                    </span>
                  ))
                )}
              </div>
            </div>
            <div className="tabs-subsection">
              <h4 className="tabs-subsection-title">Custom Tabs</h4>
              <div className="tabs-label-list">
                {selectedCustomTabs.length === 0 ? (
                  <span className="tabs-empty-hint">None selected</span>
                ) : (
                  selectedCustomTabs.map((t) => (
                    <span key={t.name} className="tab-label">
                      {t.label}
                    </span>
                  ))
                )}
              </div>
            </div>
          </div>
          <div className="permissions-header">
            <div>
              <h3>Object Permissions</h3>
            </div>
          </div>

          {(() => {
            const standardPermissions = permissions.filter((p) => p.is_standard === true);
            const customPermissions = permissions.filter((p) => p.is_standard !== true);
            const renderRow = (permission) => (
              <tr key={permission.id || permission.object_name}>
                <td className="object-cell">
                  <div className="object-name object-name-field-perms-row d-flex align-items-center justify-content-between w-100 gap-2">
                    <span className="object-name-label text-truncate min-w-0">
                      {permission.object_display_name || permission.object_name}
                    </span>
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 field-permissions-trigger flex-shrink-0"
                      title="View Field Permissions"
                      aria-label="View Field Permissions"
                      onClick={() => setFieldModalPermission(permission)}
                    >
                      <i className="fas fa-th-list" aria-hidden="true" />
                    </button>
                  </div>
                </td>
                <td className="permission-picklist-cell text-center">
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 permission-picklist-icon"
                    title="View picklist assignment, default, and order"
                    aria-label="View picklist assignment, default, and order"
                    onClick={() => setPicklistModalPermission(permission)}
                  >
                    <i className="fas fa-list-ul" aria-hidden="true" />
                  </button>
                </td>
                <td className="checkbox-cell">
                  <input type="checkbox" checked={permission.can_create || false} disabled className="permission-checkbox" />
                </td>
                <td className="checkbox-cell">
                  <input type="checkbox" checked={permission.can_read || false} disabled className="permission-checkbox" />
                </td>
                <td className="checkbox-cell">
                  <input type="checkbox" checked={permission.can_update || false} disabled className="permission-checkbox" />
                </td>
                <td className="checkbox-cell">
                  <input type="checkbox" checked={permission.can_delete || false} disabled className="permission-checkbox" />
                </td>
                <td className="checkbox-cell">
                  <input type="checkbox" checked={permission.view_all || false} disabled className="permission-checkbox" />
                </td>
                <td className="checkbox-cell">
                  <input type="checkbox" checked={permission.modify_all || false} disabled className="permission-checkbox" />
                </td>
              </tr>
            );
            return (
              <div className="permissions-table-container">
                <table className="permissions-table">
                  <thead>
                    <tr>
                      <th className="object-column text-dark">Object</th>
                      <th
                        className="permission-picklist-column text-dark text-center"
                        title="Picklists: assignment, default value, and order"
                      >
                        <i className="fas fa-list-ul" aria-hidden="true" />
                        <span className="visually-hidden">Picklists</span>
                      </th>
                      <th className="checkbox-column">
                        <div className="header-checkbox">
                          <span className='text-dark'><i className="fas fa-plus text-dark"></i> Create</span>
                        </div>
                      </th>
                      <th className="checkbox-column">
                        <div className="header-checkbox">
                          <span className='text-dark'><i className="fas fa-eye"></i> Read</span>
                        </div>
                      </th>
                      <th className="checkbox-column">
                        <div className="header-checkbox">
                          <span className='text-dark'><i className="fas fa-edit text-dark"></i> Update</span>
                        </div>
                      </th>
                      <th className="checkbox-column">
                        <div className="header-checkbox">
                          <span className='text-dark'><i className="fas fa-trash text-dark"></i> Delete</span>
                        </div>
                      </th>
                      <th className="checkbox-column">
                        <div className="header-checkbox">
                          <span className='text-dark'><i className="fas fa-eye text-dark"></i> View All</span>
                        </div>
                      </th>
                      <th className="checkbox-column">
                        <div className="header-checkbox">
                          <span className='text-dark'><i className="fas fa-edit text-dark"></i> Modify All</span>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {standardPermissions.length > 0 && (
                      <>
                        <tr className="permission-section-header">
                          <td colSpan="8" className="section-header-cell">Standard Objects</td>
                        </tr>
                        {standardPermissions.map(renderRow)}
                      </>
                    )}
                    {customPermissions.length > 0 && (
                      <>
                        <tr className="permission-section-header">
                          <td colSpan="8" className="section-header-cell">Custom Objects</td>
                        </tr>
                        {customPermissions.map(renderRow)}
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            );
          })()}
          <RecordViewLayoutTableSection
            roleId={roleId}
            permissions={permissions}
            isAdminEditingOwnRole={isAdminEditingOwnRole}
            onNotify={(variant, message) => {
              setToastVariant(variant);
              setToastMessage(message);
              setShowToast(true);
            }}
          />
        </div>
      )}

      <FieldPermissionsModal
        show={!!fieldModalPermission}
        onHide={() => setFieldModalPermission(null)}
        permission={fieldModalPermission}
        readOnly={hideSelfAdminFieldModalEdits}
        overridesForObject={
          fieldModalPermission
            ? fieldPermissionsByObject[fieldModalPermission.object_name] || {}
            : {}
        }
        onApply={hideSelfAdminFieldModalEdits ? undefined : handleListFieldPermissionsSave}
      />

      <AssignPicklistValuesModal
        show={!!picklistModalPermission}
        onHide={() => setPicklistModalPermission(null)}
        permission={picklistModalPermission}
        readOnly={hideSelfAdminFieldModalEdits}
        onApply={hideSelfAdminFieldModalEdits ? undefined : handlePicklistConfigSave}
      />

      {/* Success Toast */}
      <AppToast
        show={showToast}
        message={toastMessage}
        variant={toastVariant}
        onClose={handleToastClose}
      />
    </div>
  );
};

export default PermissionList;
